/**
 * Report Helpers — shared pure functions for all report tabs
 */

/**
 * Filter inventory items by source, customer, category, label, and search text
 */
export const filterInventoryItems = (items, filters) => {
  if (!Array.isArray(items)) return [];
  let result = [...items];

  if (filters.source && filters.source !== 'all') {
    result = result.filter(i => i.source === filters.source);
  }
  if (filters.customerId && filters.customerId !== 'all') {
    result = result.filter(i => String(i.customer_id) === String(filters.customerId));
  }
  if (filters.categoryId && filters.categoryId !== 'all') {
    result = result.filter(i => String(i.category_id) === String(filters.categoryId));
  }
  if (filters.label && filters.label !== 'all') {
    if (filters.label === '__none__') {
      result = result.filter(i => !i.label_text);
    } else if (filters.label === '__has__') {
      result = result.filter(i => !!i.label_text);
    } else {
      result = result.filter(i => i.label_text === filters.label);
    }
  }
  if (filters.search && filters.search.trim()) {
    const q = filters.search.trim().toLowerCase();
    result = result.filter(i =>
      (i.item_name && i.item_name.toLowerCase().includes(q)) ||
      (i.part_number && i.part_number.toLowerCase().includes(q)) ||
      (i.source_number && i.source_number.toLowerCase().includes(q)) ||
      (i.lot_number && i.lot_number.toLowerCase().includes(q))
    );
  }
  return result;
};

/**
 * Group items by a field, returning array of { key, label, items, totalQty, totalWeight }
 */
export const groupItems = (items, groupBy) => {
  if (!groupBy || groupBy === 'none' || !Array.isArray(items)) return null;

  const map = {};
  items.forEach(item => {
    let key, label;
    switch (groupBy) {
      case 'customer':
        key = item.customer_id || '__none__';
        label = item.customer_name || 'No Customer';
        break;
      case 'category':
        key = item.category_id || '__none__';
        label = item.category_name || 'No Category';
        break;
      case 'label':
        key = item.label_text || '__none__';
        label = item.label_text || 'No Label';
        break;
      case 'source':
        key = item.source || '__none__';
        label = (item.source || 'unknown').charAt(0).toUpperCase() + (item.source || 'unknown').slice(1);
        break;
      default:
        return;
    }
    if (!map[key]) {
      map[key] = { key, label, items: [], totalQty: 0, totalWeight: 0 };
    }
    map[key].items.push(item);
    map[key].totalQty += parseInt(item.quantity) || 0;
    map[key].totalWeight += parseFloat(item.subtotal_weight) || 0;
  });

  return Object.values(map).sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Calculate summary stats for inventory items
 */
export const calculateSummary = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return { uniqueItems: 0, totalCases: 0, totalWeight: 0, sources: {} };
  }
  const uniqueItemIds = new Set(items.map(i => i.item_id));
  const totalCases = items.reduce((sum, i) => sum + (parseInt(i.quantity) || 0), 0);
  const totalWeight = items.reduce((sum, i) => sum + (parseFloat(i.subtotal_weight) || 0), 0);

  const sources = {};
  items.forEach(i => {
    const src = i.source || 'unknown';
    sources[src] = (sources[src] || 0) + 1;
  });

  return { uniqueItems: uniqueItemIds.size, totalCases, totalWeight, sources };
};

/**
 * Export data to CSV and trigger download
 *
 * `preamble` is optional: an array of plain-text lines written above the column
 * headers, each as a single quoted cell, followed by one blank line. Callers that
 * pass only three arguments get exactly the file they got before.
 */
export const exportToCSV = (headers, rows, filename, preamble) => {
  if (!rows || rows.length === 0) return;

  let csvContent = '';
  if (Array.isArray(preamble) && preamble.length > 0) {
    preamble.forEach(line => {
      csvContent += `"${String(line ?? '').replace(/"/g, '""')}"\n`;
    });
    csvContent += '\n';
  }

  csvContent += headers.join(',') + '\n';
  rows.forEach(row => {
    csvContent += row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',') + '\n';
  });

  // Leading BOM so Excel on Windows reads the file as UTF-8
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Get distinct label values from items array
 */
export const getDistinctLabels = (items) => {
  if (!Array.isArray(items)) return [];
  const labels = new Set();
  items.forEach(i => {
    if (i.label_text) labels.add(i.label_text);
  });
  return Array.from(labels).sort();
};
